/* ---------------------------------------------------------------
   Advanced gallery — colour and size selection

   The gallery itself is driven by expanding-gallery.js. This handles
   what happens inside a panel: picking a colourway swaps the product
   image, picking a size narrows the choice, and both together resolve
   to a variant. The CTA's href then carries ?variant=<id>, so the
   product page opens with that colour and size already selected.

   Panels with no linked product carry no variant table; their chips
   still highlight, they just don't resolve to anything.
--------------------------------------------------------------- */
(function () {
  'use strict';

  function readVariants(panel) {
    if (panel.heroVariants !== undefined) return panel.heroVariants;

    var tag = panel.querySelector('[data-hero-variants]');
    if (!tag) {
      panel.heroVariants = null;
      return null;
    }

    try {
      panel.heroVariants = JSON.parse(tag.textContent);
    } catch (error) {
      panel.heroVariants = null;
    }
    return panel.heroVariants;
  }

  function selectedValue(panel, attribute) {
    var active = panel.querySelector('[' + attribute + '].is-active');
    return active ? active.getAttribute(attribute) : null;
  }

  /* A variant matches when every option we track agrees. Options we
     don't track (a third option, say) are left to the product page. */
  function matchVariant(variants, positions, colour, size) {
    for (var i = 0; i < variants.length; i += 1) {
      var options = variants[i].options || [];
      var colourOk =
        !positions.colour || !colour || options[positions.colour - 1] === colour;
      var sizeOk = !positions.size || !size || options[positions.size - 1] === size;
      if (colourOk && sizeOk) return variants[i];
    }
    return null;
  }

  function positionsFor(panel) {
    return {
      colour: parseInt(panel.getAttribute('data-colour-position'), 10) || 0,
      size: parseInt(panel.getAttribute('data-size-position'), 10) || 0
    };
  }

  function markGroup(panel, attribute, chosen) {
    Array.prototype.forEach.call(
      panel.querySelectorAll('[' + attribute + ']'),
      function (button) {
        var active = button === chosen;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    );
  }

  function showColourLayer(panel, swatch) {
    var index = swatch && swatch.getAttribute('data-hero-swatch');
    if (index === null || index === undefined) return;

    Array.prototype.forEach.call(
      panel.querySelectorAll('[data-hero-layer]'),
      function (layer) {
        layer.classList.toggle(
          'is-active',
          layer.getAttribute('data-hero-layer') === index
        );
      }
    );
  }

  /* Sizes that don't exist, or are sold out, in the chosen colour. */
  function markAvailability(panel, variants, positions, colour) {
    if (!variants || !positions.size) return;

    Array.prototype.forEach.call(
      panel.querySelectorAll('[data-hero-size]'),
      function (chip) {
        var match = matchVariant(
          variants,
          positions,
          colour,
          chip.getAttribute('data-hero-size')
        );
        chip.classList.toggle('is-unavailable', !match || !match.available);
      }
    );
  }

  function updatePanel(panel) {
    var variants = readVariants(panel);
    var positions = positionsFor(panel);

    var colour = selectedValue(panel, 'data-hero-colour');
    var size = selectedValue(panel, 'data-hero-size');

    markAvailability(panel, variants, positions, colour);

    if (!variants) return;

    var variant = matchVariant(variants, positions, colour, size);
    if (!variant) return;

    var url = panel.getAttribute('data-product-url');
    var cta = panel.querySelector('[data-hero-cta] a');
    if (cta && url) {
      cta.setAttribute('href', url + '?variant=' + variant.id);
    }

    var price = panel.querySelector('[data-hero-price]');
    if (price && variant.price) price.textContent = variant.price;

    var compare = panel.querySelector('[data-hero-compare]');
    if (compare) {
      if (variant.compare) {
        compare.textContent = variant.compare;
        compare.removeAttribute('hidden');
      } else {
        compare.setAttribute('hidden', '');
      }
    }
  }

  function handle(event, attribute) {
    var button = event.target.closest('[' + attribute + ']');
    if (!button) return null;

    var panel = button.closest('.hero-panel');
    if (!panel) return null;

    markGroup(panel, attribute, button);
    return { panel: panel, button: button };
  }

  document.addEventListener('click', function (event) {
    var colour = handle(event, 'data-hero-colour');
    if (colour) {
      event.preventDefault();
      showColourLayer(colour.panel, colour.button);
      updatePanel(colour.panel);
      return;
    }

    var size = handle(event, 'data-hero-size');
    if (size) {
      event.preventDefault();
      updatePanel(size.panel);
    }
  });



  /* ---------- hover pause ----------
     The shared script pauses while the pointer is over the active panel. In
     the hero that panel fills the whole section, so rotation would stop
     wherever the cursor sat. This narrows it to the things worth pausing
     for — the product, the copy, the chips and the controls — and lets the
     open colour of the panel go by.

     Listening on the document means this runs after the gallery's own
     handler on the way up, so whichever decision is made here is the one
     that stands. */
  var HOLDS_ROTATION = [
    '.hero-panel__product',
    '.hero-panel__copy',
    '.hero-panel__aside',
    '.hero-panel__tagline',
    '.hero-utility',
    '.hero-thumb',
    '[data-hero-tab]',
    '[data-gallery-prev]',
    '[data-gallery-next]',
    'a',
    'button'
  ].join(', ');

  document.addEventListener('mouseover', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var container = target.closest('.layout-hero');
    if (!container || typeof container.handlePause !== 'function') return;

    if (target.closest(HOLDS_ROTATION)) container.handlePause();
    else container.handleResume();
  });

  /* ---------- collection tabs ----------
     Tabs filter the hero to one group of blocks. Selection still belongs to
     the shared gallery script, so choosing a tab clicks the thumbnail of the
     first product in that group rather than reaching into its state, and the
     arrows are intercepted so they step within the group instead of walking
     out of it. */
  function panelsIn(container, group) {
    return Array.prototype.filter.call(
      container.querySelectorAll('.hero-panel'),
      function (panel) {
        return !group || panel.getAttribute('data-group') === group;
      }
    );
  }

  function indexOfPanel(container, panel) {
    return Array.prototype.indexOf.call(container.querySelectorAll('.hero-panel'), panel);
  }

  function selectPanel(container, panel) {
    var dot = container.querySelector(
      '[data-gallery-dot="' + indexOfPanel(container, panel) + '"]'
    );
    if (dot) dot.click();
  }

  /* The gallery can leave a tab by several routes the click handler never
     sees: auto-rotate, a swipe, the arrow keys. Rather than intercept each,
     this catches the result — if a selection lands outside the active tab,
     it moves on to the nearest panel inside it, continuing the direction of
     travel so a swipe still feels like a swipe. */
  function keepInGroup(container, direction) {
    var group = container.getAttribute('data-active-group') || '';
    if (!group) return false;

    var active = container.querySelector('.hero-panel.is-active');
    if (!active || active.getAttribute('data-group') === group) return false;

    var inGroup = panelsIn(container, group);
    if (!inGroup.length) return false;

    var all = Array.prototype.slice.call(container.querySelectorAll('.hero-panel'));
    var from = all.indexOf(active);
    var step = direction === 'back' ? -1 : 1;

    // Walk on in the same direction until a panel in the tab turns up.
    for (var i = 1; i <= all.length; i += 1) {
      var at = (from + step * i + all.length * i) % all.length;
      if (all[at].getAttribute('data-group') === group) {
        selectPanel(container, all[at]);
        return true;
      }
    }

    selectPanel(container, inGroup[0]);
    return true;
  }

  /* Hide what isn't in the group, and keep the corner card pointing at the
     next product inside it — the shared script flags is-next by raw index,
     which can land on a hidden panel once a tab is active. */
  function refreshTabs(container) {
    var group = container.getAttribute('data-active-group') || '';
    var inGroup = panelsIn(container, group);

    Array.prototype.forEach.call(container.querySelectorAll('.hero-panel'), function (panel) {
      var visible = !group || panel.getAttribute('data-group') === group;
      panel.classList.toggle('is-filtered', !visible);
    });

    Array.prototype.forEach.call(container.querySelectorAll('.hero-thumb'), function (thumb) {
      var visible = !group || thumb.getAttribute('data-group') === group;
      thumb.classList.toggle('is-filtered', !visible);
    });

    if (!inGroup.length) return;

    var activePanel = container.querySelector('.hero-panel.is-active');
    if (!activePanel || inGroup.indexOf(activePanel) < 0) {
      selectPanel(container, inGroup[0]);
      return;
    }

    // Back on "all products" the corner card follows the plain running order
    // again. Nothing re-selects on the way back — the active panel is already
    // valid — so the flag has to be restored here, or it stays pointing where
    // the tab left it.
    if (!group) {
      var all = Array.prototype.slice.call(container.querySelectorAll('.hero-panel'));
      var at = all.indexOf(container.querySelector('.hero-panel.is-active'));
      if (at < 0 || all.length < 2) return;

      var following = String((at + 1) % all.length);
      Array.prototype.forEach.call(container.querySelectorAll('.hero-thumb'), function (thumb) {
        thumb.classList.toggle(
          'is-next',
          thumb.getAttribute('data-gallery-dot') === following &&
            !thumb.classList.contains('is-active')
        );
      });
      return;
    }

    // A tab holding one product has nothing to tease, so the corner card goes.
    if (inGroup.length < 2) {
      Array.prototype.forEach.call(container.querySelectorAll('.hero-thumb'), function (thumb) {
        thumb.classList.remove('is-next');
      });
      return;
    }

    var next = inGroup[(inGroup.indexOf(activePanel) + 1) % inGroup.length];
    var nextIndex = String(indexOfPanel(container, next));
    Array.prototype.forEach.call(container.querySelectorAll('.hero-thumb'), function (thumb) {
      thumb.classList.toggle(
        'is-next',
        thumb.getAttribute('data-gallery-dot') === nextIndex &&
          !thumb.classList.contains('is-active')
      );
    });
  }

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-hero-tab]');
    if (!tab) return;

    var container = tab.closest('.layout-hero');
    if (!container) return;

    event.preventDefault();
    var group = tab.getAttribute('data-hero-tab') || '';
    container.setAttribute('data-active-group', group);

    Array.prototype.forEach.call(container.querySelectorAll('[data-hero-tab]'), function (other) {
      var active = other === tab;
      other.classList.toggle('is-active', active);
      other.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    refreshTabs(container);

    // Choosing a collection lands on its first product, so "all products"
    // does the same rather than staying wherever the last tab left off.
    // refreshTabs has already cleared the filters, so panel one is visible.
    if (!group) {
      var first = container.querySelector('.hero-panel');
      if (first && !first.classList.contains('is-active')) {
        selectPanel(container, first);
      }
    }
  });

  /* ---------- travel direction ----------
     Which way the product flies is decided here rather than in the shared
     gallery script, so this section keeps working whatever version of that
     file is installed. Watching is-active on the panels tells us both which
     panel is leaving and whether we moved forward or back. */
  function watchDirection(container) {
    var panels = Array.prototype.slice.call(container.querySelectorAll('.hero-panel'));
    if (panels.length < 2 || container.heroWatching) return;
    container.heroWatching = true;

    function activeIndex() {
      for (var i = 0; i < panels.length; i += 1) {
        if (panels[i].classList.contains('is-active')) return i;
      }
      return -1;
    }

    var current = Math.max(activeIndex(), 0);
    var timer;

    container.setAttribute('data-direction', 'forward');

    var observer = new MutationObserver(function () {
      var next = activeIndex();
      if (next < 0 || next === current) return;

      // Wrap-aware, so the last panel back to the first still reads forward.
      var step = next - current;
      if (step < 0) step += panels.length;
      container.setAttribute(
        'data-direction',
        step * 2 <= panels.length ? 'forward' : 'back'
      );

      var leaving = panels[current];
      panels.forEach(function (panel) {
        if (panel !== leaving) panel.classList.remove('is-leaving');
      });
      leaving.classList.add('is-leaving');

      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        leaving.classList.remove('is-leaving');
      }, 1400);

      current = next;

      // A correction re-enters here with the panel inside the tab, so this
      // settles after one bounce rather than looping.
      if (keepInGroup(container, step * 2 <= panels.length ? 'forward' : 'back')) return;

      refreshTabs(container);
    });

    panels.forEach(function (panel) {
      observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* Set the starting state once the panels are in the document, so the
     CTA already points at a real variant before anything is clicked. */
  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('.hero-panel'), updatePanel);
    Array.prototype.forEach.call(document.querySelectorAll('.layout-hero'), function (container) {
      watchDirection(container);

      // Start on whichever tab is marked active in the markup.
      var active = container.querySelector('[data-hero-tab].is-active');
      if (active) {
        container.setAttribute('data-active-group', active.getAttribute('data-hero-tab') || '');
        refreshTabs(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();